// Controlador para Catálogo de Almacenes
// Siguiendo MODO1: cat_almacen_controller
import axios from 'axios';
import { getApiUrl } from '@/config/api';
import authService from '@/shared/services/auth';

export default class CatAlmacenService {
  constructor() {
    this.almacenes = [];
    this.loading = false;
    this.error = null;
    this.selectedEmpresa = null;
  }

  // Inicializar el servicio
  async initialize() {
    // Esperar un poco para que la empresa se seleccione
    await this.waitForEmpresa();
    await this.listar();
  }

  // Esperar a que la empresa esté seleccionada
  async waitForEmpresa() {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo
    
    while (attempts < maxAttempts) {
      this.selectedEmpresa = authService.getSelectedEmpresa();
      if (this.selectedEmpresa && this.selectedEmpresa.EmpresaId) {
        console.log('Empresa encontrada para CatAlmacen:', this.selectedEmpresa);
        return;
      }
      
      // Esperar 100ms antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    // Si no se encuentra empresa después de los intentos
    this.error = 'No se pudo obtener la empresa seleccionada';
    console.error('No se pudo obtener empresa para CatAlmacen después de', maxAttempts, 'intentos');
  }

  // Listar almacenes
  async listar() {
    this.loading = true;
    this.error = null;
    
    try {
      // Verificar que tenemos empresa seleccionada
      if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
        this.selectedEmpresa = authService.getSelectedEmpresa();
        if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
          this.loading = false;
          return { success: false, data: [], message: 'Debe elegir una empresa antes de poder continuar.' };
        }
      }

      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      console.log('Cargando almacenes para empresa:', this.selectedEmpresa.EmpresaId);

      const response = await axios.post(getApiUrl('/api/CatAlmacen/listar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Filtrar por empresa en el frontend como medida de seguridad adicional
      const allAlmacenes = response.data || [];
      this.almacenes = allAlmacenes.filter(almacen => almacen.EmpresaId === this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('Almacenes cargados:', this.almacenes.length, 'de', allAlmacenes.length, 'total');
      console.log('Empresa del usuario:', this.selectedEmpresa.EmpresaId);
      
      return {
        success: true,
        data: this.almacenes,
        message: 'Almacenes cargados exitosamente'
      };
    } catch (error) {
      console.error('Error al cargar almacenes:', error);
      this.error = error.message;
      this.almacenes = [];
      this.loading = false;
      throw error;
    }
  }

  // Guardar almacén (insert/update)
  async guardar(almacen) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Asegurar que tenga la empresa seleccionada
      almacen.EmpresaId = this.selectedEmpresa.EmpresaId;

      const response = await axios.post(getApiUrl('/api/CatAlmacen/guardar'), almacen, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Recargar la lista después de guardar
      await this.listar();
      
      return {
        success: true,
        data: response.data,
        message: almacen.Clave ? 'Almacén actualizado exitosamente' : 'Almacén creado exitosamente'
      };
    } catch (error) {
      console.error('Error al guardar almacén:', error);
      throw error;
    }
  }

  // Borrar almacén
  async borrar(almacen) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatAlmacen/borrar'), almacen, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Recargar la lista después de eliminar
      await this.listar();
      
      return {
        success: true,
        data: response.data,
        message: 'Almacén eliminado exitosamente'
      };
    } catch (error) {
      console.error('Error al eliminar almacén:', error);
      throw error;
    }
  }

  // Leer un almacén específico
  async lectura(clave) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatAlmacen/lectura'), {
        Clave: clave,
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return {
        success: true,
        data: response.data,
        message: 'Almacén encontrado'
      };
    } catch (error) {
      console.error('Error al leer almacén:', error);
      throw error;
    }
  }

  // Recargar almacenes
  async recargar() {
    await this.listar();
  }

  // Descarga masiva
  async downloadBulk() {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatAlmacen/descargar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Crear blob y descargar
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `almacenes_${this.selectedEmpresa.Nombre}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error al descargar almacenes:', error);
      throw error;
    }
  }

  // Método para obtener configuración de columnas
  getDataGridColumns() {
    return [
      { key: 'Clave', label: 'Clave', sortable: true, required: true },
      { key: 'Nombre', label: 'Nombre', sortable: true, required: true },
      { key: 'Clave2', label: 'Clave 2', sortable: true },
      { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
      { 
        key: 'EsActivo', 
        label: 'Activo', 
        sortable: true,
        format: (value) => value ? 'Sí' : 'No'
      }
    ];
  }

  // Método para obtener campos del formulario
  getFormFields() {
    return [
      {
        name: 'Clave',
        label: 'Clave',
        type: 'text',
        required: true,
        maxlength: 50
      },
      {
        name: 'Nombre',
        label: 'Nombre',
        type: 'text',
        required: true,
        maxlength: 100
      },
      {
        name: 'Clave2',
        label: 'Clave 2',
        type: 'text',
        maxlength: 50
      },
      {
        name: 'ClaveErp',
        label: 'Clave ERP',
        type: 'text',
        maxlength: 50
      },
      {
        name: 'EsActivo',
        label: 'Activo',
        type: 'checkbox',
        default: true
      }
    ];
  }
}
